import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import tailwind from "./tailwind.css?url";
import brandStyles from "./styles/brand.css?url";

export async function loader() {
  return {
    ENV: {
      SHOPIFY_API_KEY: process.env.SHOPIFY_API_KEY,
      NODE_ENV: process.env.NODE_ENV,
    },
  };
}

export const links = () => [
  { rel: "stylesheet", href: tailwind },
  { rel: "stylesheet", href: brandStyles },
  { rel: "icon", type: "image/png", href: "/radar-square.png" },
];

export default function App() {
  const { ENV } = useLoaderData();

  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />

        {/* App Bridge Integration - Required for Built for Shopify */}
        <meta name="shopify-api-key" content={ENV.SHOPIFY_API_KEY} />

        {/* Web Vitals Debug Mode - Development Only */}
        {ENV.NODE_ENV === "development" && (
          <meta name="shopify-debug" content="web-vitals" />
        )}

        <link rel="preconnect" href="https://cdn.shopify.com/" />
        <link
          rel="stylesheet"
          href="https://cdn.shopify.com/static/fonts/inter/v4/styles.css"
        />
        <Meta />
        <Links />

        {/*
          Polaris Web Components runtime + Web Vitals reporter.

          Polaris is loaded via DOM-appended <script> rather than a static <script> element
          inside the React tree. This avoids hydration mismatches: a blocking <script> in
          <head> can mutate the DOM (preload tags, style injections) before React hydrates,
          causing the server-rendered tree and live DOM to diverge.

          The inline script below runs at parse time, appends polaris.js to <head>, and
          polaris.js loads with normal browser priority. React only ever sees this stable
          inline <script> element in its tree; the dynamically-appended polaris script is
          outside React's reconciliation.

          The :not(:defined) CSS rule hides unupgraded custom elements briefly so the page
          doesn't flash unstyled <s-*> tags before polaris.js registers them.
        */}
        <style
          dangerouslySetInnerHTML={{
            __html: `s-page:not(:defined),s-stack:not(:defined),s-section:not(:defined),s-button:not(:defined),s-text:not(:defined),s-heading:not(:defined),s-banner:not(:defined),s-box:not(:defined),s-badge:not(:defined),s-icon:not(:defined),s-thumbnail:not(:defined),s-spinner:not(:defined),s-link:not(:defined),s-button-group:not(:defined),s-modal:not(:defined),s-text-field:not(:defined),s-text-area:not(:defined),s-select:not(:defined),s-option:not(:defined),s-checkbox:not(:defined),s-divider:not(:defined){visibility:hidden}`
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              // Append Polaris WC runtime outside React's tree.
              (function () {
                if (document.querySelector('script[data-polaris-wc]')) return;
                var s = document.createElement('script');
                s.src = 'https://cdn.shopify.com/shopifycloud/polaris.js';
                s.setAttribute('data-polaris-wc', '1');
                document.head.appendChild(s);
              })();

              // Web Vitals monitoring for Built for Shopify compliance.
              // Wait for shopify.webVitals to be available (set by App Bridge after polaris.js).
              (function () {
                function tryRegister() {
                  if (typeof shopify !== 'undefined' && shopify.webVitals) {
                    shopify.webVitals.onReport(function (metrics) {
                      fetch('/api/web-vitals', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(metrics)
                      }).catch(function (err) { console.warn('[Web Vitals] Failed to send:', err); });
                    });
                    return true;
                  }
                  return false;
                }
                if (!tryRegister()) {
                  var attempts = 0;
                  var iv = setInterval(function () {
                    attempts++;
                    if (tryRegister() || attempts > 50) clearInterval(iv);
                  }, 100);
                }
              })();
            `
          }}
        />
      </head>
      <body>
        <Outlet />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const isResponse = isRouteErrorResponse(error);
  const status = isResponse ? error.status : 500;
  const isNotFound = isResponse && status === 404;
  const isDev =
    typeof process !== "undefined" && process.env?.NODE_ENV !== "production";
  const devMessage = !isResponse && error?.message ? String(error.message) : null;
  const devStack = !isResponse && error?.stack ? String(error.stack) : null;

  const heading = isNotFound ? "Page not found" : "Something went wrong";
  const body = isNotFound
    ? "We can't find that page. Try going back to the dashboard."
    : "RADAR hit an unexpected error. We've been notified. Try refreshing, or reach out if it keeps happening.";

  return (
    <html lang="en">
      <head>
        <title>{heading}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <main style={errPage}>
          <section style={errPanel}>
            <h1 style={errH1}>{heading}</h1>
            <p style={errBody}>{body}</p>
            <div style={errActions}>
              {!isNotFound ? (
                <button
                  type="button"
                  style={errButton}
                  onClick={() => {
                    if (typeof window !== "undefined") window.location.reload();
                  }}
                >
                  Reload
                </button>
              ) : null}
              <a href="/app" style={isNotFound ? errButton : errButtonSecondary}>
                Back to dashboard
              </a>
            </div>
            <p style={errMeta}>
              <a href="mailto:support@radar.aquivolabs.com" style={errLink}>
                support@radar.aquivolabs.com
              </a>
            </p>
            {isDev && devMessage ? (
              <details style={errDetails}>
                <summary style={errSummary}>Developer details</summary>
                <pre style={errPre}>{devMessage}</pre>
                {devStack ? <pre style={errPre}>{devStack}</pre> : null}
              </details>
            ) : null}
          </section>
        </main>
        <Scripts />
      </body>
    </html>
  );
}

const errPage = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background: "#f9fafb",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  padding: 24,
};
const errPanel = {
  width: "min(520px, 100%)",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 28,
  boxShadow: "0 12px 30px rgba(17,24,39,0.08)",
};
const errH1 = { margin: "0 0 12px", fontSize: 24, color: "#111827" };
const errBody = { margin: "0 0 20px", fontSize: 15, lineHeight: 1.5, color: "#374151" };
const errActions = { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" };
const errButton = {
  display: "inline-block",
  padding: "10px 14px",
  borderRadius: 6,
  background: "#2563eb",
  color: "#fff",
  border: 0,
  fontSize: 14,
  fontWeight: 600,
  textDecoration: "none",
  cursor: "pointer",
};
const errButtonSecondary = {
  display: "inline-block",
  padding: "10px 14px",
  borderRadius: 6,
  background: "#fff",
  color: "#2563eb",
  border: "1px solid #2563eb",
  fontSize: 14,
  fontWeight: 600,
  textDecoration: "none",
  cursor: "pointer",
};
const errMeta = { margin: 0, fontSize: 13, color: "#6b7280" };
const errLink = { color: "#2563eb", textDecoration: "none" };
const errDetails = {
  marginTop: 20,
  padding: 12,
  background: "#f3f4f6",
  borderRadius: 6,
  fontSize: 12,
  color: "#374151",
};
const errSummary = { cursor: "pointer", fontWeight: 600, marginBottom: 8 };
const errPre = {
  margin: "8px 0 0",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
};
